import * as THREE from 'three';
import { GeometryUtils } from '../../../../utils/geometry/GeometryUtils';
import { InsideType, ModuleType } from '../../../../constants/enum';

export default class DragMolduleController {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
    this.canvas = sceneManager.canvas;
    this.scene = sceneManager.scene;
    this.camera = sceneManager.camera;
    this.raycaster = sceneManager.raycaster;
    this.mouse = sceneManager.mouse;

    this.selectedModel = null;
    this.selectedModelData = null;

    this._btnWidth = -30;
    this._dragging = false;
    this._dragAxis = null;
    this._initialOffset = null;
    this._lastValidPosition = null;

    // Snap positioning properties
    this._snapEnabled = true;
    this._snapTolerance = 0.3; // Distance threshold for snapping (in world units)
    this._adjacentThreshold = 0.2; // Distance to consider models as touching
    this._snapLinesMaterial = null;
    this._snapLines = [];
    this._currentSnapTarget = null;
    this._currentSnapPosition = null;
    this._snapIndicatorCallback = null;

    // Snap positions enum dựa trên 6 mặt của hình hộp chữ nhật
    this.SNAP_POSITIONS = {
      // Adjacent positioning (liền kề)
      ADJACENT_LEFT: 'adjacent_left', // Đặt liền cạnh trái
      ADJACENT_RIGHT: 'adjacent_right', // Đặt liền cạnh phải
      ADJACENT_FRONT: 'adjacent_front', // Đặt liền cạnh trước
      ADJACENT_BACK: 'adjacent_back', // Đặt liền cạnh sau
      ADJACENT_TOP: 'adjacent_top', // Đặt liền mặt trên
      ADJACENT_BOTTOM: 'adjacent_bottom', // Đặt liền mặt dưới

      // Edge alignment (thẳng cạnh)
      ALIGN_LEFT_EDGE: 'align_left_edge', // Thẳng cạnh trái
      ALIGN_RIGHT_EDGE: 'align_right_edge', // Thẳng cạnh phải
      ALIGN_FRONT_EDGE: 'align_front_edge', // Thẳng cạnh trước
      ALIGN_BACK_EDGE: 'align_back_edge', // Thẳng cạnh sau
      ALIGN_TOP_EDGE: 'align_top_edge', // Thẳng cạnh trên
      ALIGN_BOTTOM_EDGE: 'align_bottom_edge', // Thẳng cạnh dưới

      // Center alignment
      CENTER_ALIGN: 'center_align', // Căn giữa tâm

      //Fit column (khớp cột)
      FIT_COLUMN: 'fit_column', // Khớp vị trí cột
    };

    // Enum cho 6 mặt của hình hộp chữ nhật
    this.BOX_FACES = {
      LEFT: 'left', // Mặt trái (X min)
      RIGHT: 'right', // Mặt phải (X max)
      FRONT: 'front', // Mặt trước (Z min)
      BACK: 'back', // Mặt sau (Z max)
      BOTTOM: 'bottom', // Mặt dưới (Y min)
      TOP: 'top', // Mặt trên (Y max)
    };

    // Tạo drag plane ảo
    this._dragPlane = null;
    this._createDragPlane();

    // Callbacks
    this._onUISelect = null;
    this._onUIDeselect = null;
    this._onUIDragging = null;
    this._onUIDragEnd = null;

    this.initEvents();
    this._createSnapLinesMaterial();
  }

  // Tạo material cho snap lines
  _createSnapLinesMaterial() {
    this._snapLinesMaterial = new THREE.LineBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.8,
      linewidth: 2,
    });
  }

  // Tạo plane ảo để drag, kích thước cố định
  _createDragPlane(y) {
    const planeGeometry = new THREE.PlaneGeometry(100, 100);

    const planeMaterial = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      color: 0x00ffff, // Màu cyan
      transparent: true,
      depthWrite: false,
      opacity: 0,
    });

    // Tạo plane mesh
    this._dragPlane = new THREE.Mesh(planeGeometry, planeMaterial);

    // Đặt vị trí plane cao hơn sàn một chút để dễ thấy
    this._dragPlane.position.set(0, 0, 0);
    if (y) {
      this._dragPlane.position.set(0, y, 0);
    }
    this._dragPlane.rotation.x = -Math.PI / 2; // Xoay để nằm ngang

    // Đặt userData để nhận diện
    this._dragPlane.userData = { isDragPlane: true };

    // Đảm bảo plane luôn được render
    this._dragPlane.renderOrder = 1;

    // Thêm vào scene
    this.scene.add(this._dragPlane);
  }

  // Cập nhật drag plane khi sàn thay đổi
  updateDragPlane(y) {
    if (this._dragPlane) {
      this.scene.remove(this._dragPlane);
      this._dragPlane.geometry.dispose();
      this._dragPlane.material.dispose();
    }
    this._createDragPlane(y);
  }

  // Kiểm tra 2 model có tiếp xúc/liền kề không
  areModelsAdjacent(model1, model2, adjacentThreshold = null) {
    if (!model1 || !model2) return false;

    const threshold = adjacentThreshold || this._adjacentThreshold;
    const box1 = this.getUpdatedBoundingBox(model1);
    const box2 = this.getUpdatedBoundingBox(model2);

    if (!box1 || !box2) return false;

    // Tính khoảng cách giữa các mặt của 2 bounding box
    const distances = {
      // Khoảng cách theo trục X
      x: Math.max(
        0,
        Math.max(box1.min.x - box2.max.x, box2.min.x - box1.max.x)
      ),
      // Khoảng cách theo trục Y
      y: Math.max(
        0,
        Math.max(box1.min.y - box2.max.y, box2.min.y - box1.max.y)
      ),
      // Khoảng cách theo trục Z
      z: Math.max(
        0,
        Math.max(box1.min.z - box2.max.z, box2.min.z - box1.max.z)
      ),
    };

    // Kiểm tra overlap ở các trục
    const xOverlap = box1.max.x >= box2.min.x && box2.max.x >= box1.min.x;
    const yOverlap = box1.max.y >= box2.min.y && box2.max.y >= box1.min.y;
    const zOverlap = box1.max.z >= box2.min.z && box2.max.z >= box1.min.z;

    // Models được coi là adjacent nếu tiếp xúc ít nhất ở một trục và có overlap ở các trục còn lại
    const xTouch = distances.x <= threshold && yOverlap && zOverlap;
    const yTouch = distances.y <= threshold && xOverlap && zOverlap;
    const zTouch = distances.z <= threshold && xOverlap && yOverlap;

    return xTouch || yTouch || zTouch;
  }

  // Phát hiện mặt nào của 2 model đang tiếp xúc
  // detectTouchingFaces(model1, model2, tolerance = 0.05) {
  detectTouchingFaces(model1, model2, tolerance = 0.01) {
    if (!model1 || !model2) return null;

    const box1 = this.getUpdatedBoundingBox(model1);
    const box2 = this.getUpdatedBoundingBox(model2);

    if (!box1 || !box2) return null;

    const touchingInfo = {
      isTouching: false,
      model1Face: null, // Mặt nào của model1 đang tiếp xúc
      model2Face: null, // Mặt nào của model2 đang tiếp xúc
      direction: null, // Hướng tiếp xúc (x, y, z)
      gap: 0, // Khoảng cách giữa 2 mặt
    };

    // Kiểm tra tiếp xúc theo từng cặp mặt
    const faceChecks = [
      // Model1 mặt phải - Model2 mặt trái (tiếp xúc theo trục X)
      {
        model1Face: this.BOX_FACES.RIGHT,
        model2Face: this.BOX_FACES.LEFT,
        gap: Math.abs(box1.max.x - box2.min.x),
        direction: 'x',
        overlap: this.checkOverlap(box1, box2, ['y', 'z']),
      },
      // Model1 mặt trái - Model2 mặt phải (tiếp xúc theo trục X)
      {
        model1Face: this.BOX_FACES.LEFT,
        model2Face: this.BOX_FACES.RIGHT,
        gap: Math.abs(box1.min.x - box2.max.x),
        direction: 'x',
        overlap: this.checkOverlap(box1, box2, ['y', 'z']),
      },
      // Model1 mặt sau - Model2 mặt trước (tiếp xúc theo trục Z)
      {
        model1Face: this.BOX_FACES.BACK,
        model2Face: this.BOX_FACES.FRONT,
        gap: Math.abs(box1.max.z - box2.min.z),
        direction: 'z',
        overlap: this.checkOverlap(box1, box2, ['x', 'y']),
      },
      // Model1 mặt trước - Model2 mặt sau (tiếp xúc theo trục Z)
      {
        model1Face: this.BOX_FACES.FRONT,
        model2Face: this.BOX_FACES.BACK,
        gap: Math.abs(box1.min.z - box2.max.z),
        direction: 'z',
        overlap: this.checkOverlap(box1, box2, ['x', 'y']),
      },
      // Model1 mặt trên - Model2 mặt dưới (tiếp xúc theo trục Y)
      {
        model1Face: this.BOX_FACES.TOP,
        model2Face: this.BOX_FACES.BOTTOM,
        gap: Math.abs(box1.max.y - box2.min.y),
        direction: 'y',
        overlap: this.checkOverlap(box1, box2, ['x', 'z']),
      },
      // Model1 mặt dưới - Model2 mặt trên (tiếp xúc theo trục Y)
      {
        model1Face: this.BOX_FACES.BOTTOM,
        model2Face: this.BOX_FACES.TOP,
        gap: Math.abs(box1.min.y - box2.max.y),
        direction: 'y',
        overlap: this.checkOverlap(box1, box2, ['x', 'z']),
      },
    ];

    // Tìm mặt tiếp xúc gần nhất có overlap
    let closestTouch = null;
    let minGap = tolerance;

    for (const check of faceChecks) {
      if (check.overlap && check.gap <= minGap) {
        minGap = check.gap;
        closestTouch = check;
      }
    }

    if (closestTouch) {
      touchingInfo.isTouching = true;
      touchingInfo.model1Face = closestTouch.model1Face;
      touchingInfo.model2Face = closestTouch.model2Face;
      touchingInfo.direction = closestTouch.direction;
      touchingInfo.gap = closestTouch.gap;
    }

    return touchingInfo;
  }

  // Kiểm tra overlap giữa 2 box theo các trục được chỉ định
  checkOverlap(box1, box2, axes) {
    for (const axis of axes) {
      let overlap = false;

      switch (axis) {
        case 'x':
          overlap = box1.max.x > box2.min.x && box2.max.x > box1.min.x;
          break;
        case 'y':
          overlap = box1.max.y > box2.min.y && box2.max.y > box1.min.y;
          break;
        case 'z':
          overlap = box1.max.z > box2.min.z && box2.max.z > box1.min.z;
          break;
      }

      if (!overlap) return false;
    }

    return true;
  }

  // Tìm tất cả các model có thể snap với model hiện tại (chỉ những model đang tiếp xúc)
  findSnapTargets(draggedModel) {
    const snapTargets = [];
    const allModels = this.scene.children.filter(
      child => child.userData?.type === 'module' && child !== draggedModel
      // child =>
      //   child.userData?.requireCollision === true && child !== draggedModel
    );

    for (const model of allModels) {
      // Chỉ thêm vào snap targets nếu model đang tiếp xúc hoặc rất gần
      if (this.areModelsAdjacent(draggedModel, model)) {
        snapTargets.push(model);
      }
    }

    return snapTargets;
  }

  // Tìm tất cả các column có thể snap với model hiện tại (chỉ những column đang tiếp xúc)
  findColumnSnapTargets(draggedModel) {
    const snapTargets = [];
    const allModels = this.scene.children.filter(
      child => child.userData?.isColumn === true
    );

    for (const model of allModels) {
      // Chỉ thêm vào snap targets nếu model đang tiếp xúc hoặc rất gần
      if (this.areModelsAdjacent(draggedModel, model)) {
        snapTargets.push(model);
      }
    }

    return snapTargets;
  }

  getSize(object3D) {
    if (!object3D) return null;

    const box = new THREE.Box3().setFromObject(object3D);

    const size = new THREE.Vector3();
    box.getSize(size);

    const min = box.min.clone();
    const max = box.max.clone();

    return {
      x: size.x,
      y: size.y,
      z: size.z,
      min,
      max,
    };
  }

  // Tính toán vị trí snap với cột dựa trên mặt tiếp xúc
  calculateColumnSnapPositions(draggedModel, targetModel) {
    if (!draggedModel || !targetModel) return [];

    // Kiểm tra xem 2 model có đang tiếp xúc không
    if (!this.areModelsAdjacent(draggedModel, targetModel)) {
      return [];
    }

    // Phát hiện mặt nào đang tiếp xúc
    const touchingInfo = this.detectTouchingFaces(draggedModel, targetModel);
    if (!touchingInfo || !touchingInfo.isTouching) {
      return [];
    }

    const draggedBox = this.getUpdatedBoundingBox(draggedModel);
    const targetBox = this.getUpdatedBoundingBox(targetModel);

    const draggedSize = this.getSize(
      GeometryUtils.moduleWithoutAccessory(draggedModel)
    );
    const targetSize = this.getSize(targetModel);

    if (!draggedBox || !targetBox) return [];

    const snapPositions = [];
    // const minGap = 0.05;
    const minGap = 0;

    // Dựa trên mặt tiếp xúc để recommend vị trí snap
    const model1Face = touchingInfo.model1Face; // Mặt của draggedModel
    const model2Face = touchingInfo.model2Face; // Mặt của targetModel
    const direction = touchingInfo.direction;

    const tolerance = 0.0005; // Sai số cho phép khi so sánh kích thước
    const rongCot =
      this.selectedModelData?.formularController?.currentOptions?.rongCot;
    const dayCot =
      this.selectedModelData?.formularController?.currentOptions?.dayCot;
    const KCCot =
      this.selectedModelData?.formularController?.currentOptions?.KCCot || 0;

    const rcmPosition = new THREE.Vector3(
      targetModel.position.x - targetSize.x / 2 + draggedSize.x / 2,
      draggedModel.position.y,
      targetModel.position.z + targetSize.z / 2 + KCCot - draggedSize.z / 2
    );
    const rcmPositionR270 = new THREE.Vector3(
      targetModel.position.x - targetSize.x / 2 - KCCot + draggedSize.x / 2,
      draggedModel.position.y,
      targetModel.position.z - targetSize.z / 2 + draggedSize.z / 2
    );

    let isMatch = false;
    let isNear = false;

    // //Kiểm tra chính xác
    // if (draggedModel.rotation.y === 0) {
    //   isMatch =
    //     Math.abs(targetSize.x - dayCot) < tolerance &&
    //     Math.abs(targetSize.z - rongCot) < tolerance;

    //   isNear = Math.abs(draggedModel.position.z - rcmPosition.z) < 0.005;
    // } else {
    //   isMatch =
    //     Math.abs(targetSize.z - dayCot) < tolerance &&
    //     Math.abs(targetSize.x - rongCot) < tolerance;

    //   isNear = Math.abs(draggedModel.position.x - rcmPositionR270.x) < 0.005;
    // }

    //Kiểm tra chỉ cần kích thước khuyết lớn hơn cột là được
    if (draggedModel.rotation.y === 0) {
      isMatch =
        dayCot - targetSize.x + tolerance > 0 &&
        rongCot - targetSize.z + tolerance > 0;

      isNear = Math.abs(draggedModel.position.z - rcmPosition.z) < 0.005;
    } else {
      isMatch =
        dayCot - targetSize.z + tolerance > 0 &&
        rongCot - targetSize.x + tolerance > 0;

      isNear = Math.abs(draggedModel.position.x - rcmPositionR270.x) < 0.005;
    }

    if (!isMatch || !isNear) return [];

    // Snap thẳng tới vị trí cột của module khuyết cột
    const edgeAlignments = [
      {
        type: this.SNAP_POSITIONS.FIT_COLUMN,
        position: rcmPosition,

        target: targetModel,
        description: 'Khớp vị trí cột',
      },
    ];
    const edgeAlignmentsR270 = [
      {
        type: this.SNAP_POSITIONS.FIT_COLUMN,
        position: rcmPositionR270,

        target: targetModel,
        description: 'Khớp vị trí cột',
      },
    ];

    if (draggedModel.rotation.y === 0) {
      snapPositions.push(...edgeAlignments);
    } else {
      snapPositions.push(...edgeAlignmentsR270);
    }

    return snapPositions;
  }

  // Tính toán vị trí snap dựa trên mặt tiếp xúc
  calculateSnapPositions(draggedModel, targetModel) {
    if (!draggedModel || !targetModel) return [];

    // Kiểm tra xem 2 model có đang tiếp xúc không
    if (!this.areModelsAdjacent(draggedModel, targetModel)) {
      return [];
    }

    // Phát hiện mặt nào đang tiếp xúc
    const touchingInfo = this.detectTouchingFaces(draggedModel, targetModel);
    if (!touchingInfo || !touchingInfo.isTouching) {
      return [];
    }

    const draggedBox = this.getUpdatedBoundingBox(draggedModel);
    const targetBox = this.getUpdatedBoundingBox(targetModel);

    if (!draggedBox || !targetBox) return [];

    const snapPositions = [];
    // const minGap = 0.05;
    const minGap = 0;

    // Dựa trên mặt tiếp xúc để recommend vị trí snap
    const model1Face = touchingInfo.model1Face; // Mặt của draggedModel
    const model2Face = touchingInfo.model2Face; // Mặt của targetModel
    const direction = touchingInfo.direction;

    // 1. ADJACENT POSITIONING - Đặt liền kề dựa trên mặt tiếp xúc
    const adjacentPosition = this.calculateAdjacentPosition(
      draggedModel,
      targetModel,
      model1Face,
      model2Face,
      minGap
    );

    if (adjacentPosition) {
      snapPositions.push({
        type: this.getAdjacentSnapType(model2Face),
        position: adjacentPosition,
        target: targetModel,
        description: this.getAdjacentDescription(model2Face),
      });
    }

    // 2. EDGE ALIGNMENT - Thẳng cạnh dựa trên hướng tiếp xúc
    const edgeAlignments = this.calculateEdgeAlignments(
      draggedModel,
      targetModel,
      direction,
      model1Face,
      model2Face
    );

    snapPositions.push(...edgeAlignments);

    // 3. CENTER ALIGNMENT - Căn giữa theo hướng vuông góc với mặt tiếp xúc
    const centerAlignment = this.calculateCenterAlignment(
      draggedModel,
      targetModel,
      direction
    );

    if (centerAlignment) {
      snapPositions.push(centerAlignment);
    }

    return snapPositions;
  }

  // Tính toán vị trí adjacent (liền kề)
  calculateAdjacentPosition(
    draggedModel,
    targetModel,
    model1Face,
    model2Face,
    gap
  ) {
    const draggedBox = this.getUpdatedBoundingBox(draggedModel);
    const targetBox = this.getUpdatedBoundingBox(targetModel);

    let adjacentPosition = null;

    switch (model2Face) {
      case this.BOX_FACES.LEFT:
        // Đặt draggedModel bên trái targetModel
        const leftX =
          targetBox.min.x - (draggedBox.max.x - draggedBox.min.x) - gap;
        adjacentPosition = new THREE.Vector3(
          leftX + (draggedModel.position.x - draggedBox.min.x),
          draggedModel.position.y,
          targetModel.position.z
        );
        break;

      case this.BOX_FACES.RIGHT:
        // Đặt draggedModel bên phải targetModel
        const rightX = targetBox.max.x + gap;
        adjacentPosition = new THREE.Vector3(
          rightX + (draggedModel.position.x - draggedBox.min.x),
          draggedModel.position.y,
          targetModel.position.z
        );
        break;

      case this.BOX_FACES.FRONT:
        // Đặt draggedModel phía trước targetModel
        const frontZ =
          targetBox.min.z - (draggedBox.max.z - draggedBox.min.z) - gap;
        adjacentPosition = new THREE.Vector3(
          targetModel.position.x,
          draggedModel.position.y,
          frontZ + (draggedModel.position.z - draggedBox.min.z)
        );
        break;

      case this.BOX_FACES.BACK:
        // Đặt draggedModel phía sau targetModel
        const backZ = targetBox.max.z + gap;
        adjacentPosition = new THREE.Vector3(
          targetModel.position.x,
          draggedModel.position.y,
          backZ + (draggedModel.position.z - draggedBox.min.z)
        );
        break;

      case this.BOX_FACES.BOTTOM:
        // Đặt draggedModel phía dưới targetModel
        const bottomY =
          targetBox.min.y - (draggedBox.max.y - draggedBox.min.y) - gap;
        adjacentPosition = new THREE.Vector3(
          targetModel.position.x,
          bottomY + (draggedModel.position.y - draggedBox.min.y),
          targetModel.position.z
        );
        break;

      case this.BOX_FACES.TOP:
        // Đặt draggedModel phía trên targetModel
        const topY = targetBox.max.y + gap;
        adjacentPosition = new THREE.Vector3(
          targetModel.position.x,
          topY + (draggedModel.position.y - draggedBox.min.y),
          targetModel.position.z
        );
        break;
    }

    return adjacentPosition;
  }

  // Tính toán edge alignments (thẳng cạnh)
  calculateEdgeAlignments(
    draggedModel,
    targetModel,
    direction,
    model1Face,
    model2Face
  ) {
    const draggedBox = this.getUpdatedBoundingBox(draggedModel);
    const targetBox = this.getUpdatedBoundingBox(targetModel);
    const alignments = [];

    switch (direction) {
      case 'x': // Tiếp xúc theo trục X
        // Thẳng cạnh trước/sau (Z alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_FRONT_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            draggedModel.position.y,
            targetBox.min.z + (draggedModel.position.z - draggedBox.min.z)
          ),
          target: targetModel,
          description: 'Thẳng cạnh trước',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_BACK_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            draggedModel.position.y,
            targetBox.max.z - (draggedBox.max.z - draggedModel.position.z)
          ),
          target: targetModel,
          description: 'Thẳng cạnh sau',
        });

        // Thẳng cạnh trên/dưới (Y alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_BOTTOM_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            targetBox.min.y + (draggedModel.position.y - draggedBox.min.y),
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh dưới',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_TOP_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            targetBox.max.y - (draggedBox.max.y - draggedModel.position.y),
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh trên',
        });
        break;

      case 'z': // Tiếp xúc theo trục Z
        // Thẳng cạnh trái/phải (X alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_LEFT_EDGE,
          position: new THREE.Vector3(
            targetBox.min.x + (draggedModel.position.x - draggedBox.min.x),
            draggedModel.position.y,
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh trái',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_RIGHT_EDGE,
          position: new THREE.Vector3(
            targetBox.max.x - (draggedBox.max.x - draggedModel.position.x),
            draggedModel.position.y,
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh phải',
        });

        // Thẳng cạnh trên/dưới (Y alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_BOTTOM_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            targetBox.min.y + (draggedModel.position.y - draggedBox.min.y),
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh dưới',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_TOP_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            targetBox.max.y - (draggedBox.max.y - draggedModel.position.y),
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh trên',
        });
        break;

      case 'y': // Tiếp xúc theo trục Y
        // Thẳng cạnh trái/phải (X alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_LEFT_EDGE,
          position: new THREE.Vector3(
            targetBox.min.x + (draggedModel.position.x - draggedBox.min.x),
            draggedModel.position.y,
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh trái',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_RIGHT_EDGE,
          position: new THREE.Vector3(
            targetBox.max.x - (draggedBox.max.x - draggedModel.position.x),
            draggedModel.position.y,
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Thẳng cạnh phải',
        });

        // Thẳng cạnh trước/sau (Z alignment)
        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_FRONT_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            draggedModel.position.y,
            targetBox.min.z + (draggedModel.position.z - draggedBox.min.z)
          ),
          target: targetModel,
          description: 'Thẳng cạnh trước',
        });

        alignments.push({
          type: this.SNAP_POSITIONS.ALIGN_BACK_EDGE,
          position: new THREE.Vector3(
            draggedModel.position.x,
            draggedModel.position.y,
            targetBox.max.z - (draggedBox.max.z - draggedModel.position.z)
          ),
          target: targetModel,
          description: 'Thẳng cạnh sau',
        });
        break;
    }

    return alignments;
  }

  // Tính toán center alignment
  calculateCenterAlignment(draggedModel, targetModel, direction) {
    switch (direction) {
      case 'x': // Tiếp xúc theo X, căn giữa theo Y và Z
        return {
          type: this.SNAP_POSITIONS.CENTER_ALIGN,
          position: new THREE.Vector3(
            draggedModel.position.x,
            targetModel.position.y,
            targetModel.position.z
          ),
          target: targetModel,
          description: 'Căn giữa tâm',
        };

      case 'z': // Tiếp xúc theo Z, căn giữa theo X và Y
        return {
          type: this.SNAP_POSITIONS.CENTER_ALIGN,
          position: new THREE.Vector3(
            targetModel.position.x,
            targetModel.position.y,
            draggedModel.position.z
          ),
          target: targetModel,
          description: 'Căn giữa tâm',
        };

      case 'y': // Tiếp xúc theo Y, căn giữa theo X và Z
        return {
          type: this.SNAP_POSITIONS.CENTER_ALIGN,
          position: new THREE.Vector3(
            targetModel.position.x,
            draggedModel.position.y,
            targetModel.position.z
          ),
          target: targetModel,
          description: 'Căn giữa tâm',
        };
    }

    return null;
  }

  // Helper methods để lấy snap type và description
  getAdjacentSnapType(face) {
    switch (face) {
      case this.BOX_FACES.LEFT:
        return this.SNAP_POSITIONS.ADJACENT_LEFT;
      case this.BOX_FACES.RIGHT:
        return this.SNAP_POSITIONS.ADJACENT_RIGHT;
      case this.BOX_FACES.FRONT:
        return this.SNAP_POSITIONS.ADJACENT_FRONT;
      case this.BOX_FACES.BACK:
        return this.SNAP_POSITIONS.ADJACENT_BACK;
      case this.BOX_FACES.TOP:
        return this.SNAP_POSITIONS.ADJACENT_TOP;
      case this.BOX_FACES.BOTTOM:
        return this.SNAP_POSITIONS.ADJACENT_BOTTOM;
      default:
        return this.SNAP_POSITIONS.CENTER_ALIGN;
    }
  }

  getAdjacentDescription(face) {
    switch (face) {
      case this.BOX_FACES.LEFT:
        return 'Đặt liền cạnh trái';
      case this.BOX_FACES.RIGHT:
        return 'Đặt liền cạnh phải';
      case this.BOX_FACES.FRONT:
        return 'Đặt liền cạnh trước';
      case this.BOX_FACES.BACK:
        return 'Đặt liền cạnh sau';
      case this.BOX_FACES.TOP:
        return 'Đặt liền mặt trên';
      case this.BOX_FACES.BOTTOM:
        return 'Đặt liền mặt dưới';
      default:
        return 'Đặt liền kề';
    }
  }

  // Cập nhật method getTouchingModels để sử dụng detectTouchingFaces
  getTouchingModels(targetModel) {
    if (!targetModel) return [];

    const touchingModels = [];
    const allModels = this.scene.children.filter(
      // child => child.userData?.type === 'module' && child !== targetModel
      child =>
        child.userData?.requireCollision === true && child !== targetModel
    );

    for (const model of allModels) {
      if (this.areModelsAdjacent(targetModel, model)) {
        const touchingInfo = this.detectTouchingFaces(targetModel, model);
        touchingModels.push({
          model: model,
          adjacent: true,
          exactlyTouching: touchingInfo.isTouching,
          model1Face: touchingInfo.model1Face,
          model2Face: touchingInfo.model2Face,
          direction: touchingInfo.direction,
          gap: touchingInfo.gap,
        });
      }
    }

    return touchingModels;
  }

  // Cập nhật helper method để lấy description dựa trên các snap type mới
  getSnapDescription(snapType) {
    switch (snapType) {
      case this.SNAP_POSITIONS.ADJACENT_LEFT:
        return 'Đặt liền cạnh trái';
      case this.SNAP_POSITIONS.ADJACENT_RIGHT:
        return 'Đặt liền cạnh phải';
      case this.SNAP_POSITIONS.ADJACENT_FRONT:
        return 'Đặt liền cạnh trước';
      case this.SNAP_POSITIONS.ADJACENT_BACK:
        return 'Đặt liền cạnh sau';
      case this.SNAP_POSITIONS.ADJACENT_TOP:
        return 'Đặt liền mặt trên';
      case this.SNAP_POSITIONS.ADJACENT_BOTTOM:
        return 'Đặt liền mặt dưới';
      case this.SNAP_POSITIONS.ALIGN_LEFT_EDGE:
        return 'Thẳng cạnh trái';
      case this.SNAP_POSITIONS.ALIGN_RIGHT_EDGE:
        return 'Thẳng cạnh phải';
      case this.SNAP_POSITIONS.ALIGN_FRONT_EDGE:
        return 'Thẳng cạnh trước';
      case this.SNAP_POSITIONS.ALIGN_BACK_EDGE:
        return 'Thẳng cạnh sau';
      case this.SNAP_POSITIONS.ALIGN_TOP_EDGE:
        return 'Thẳng cạnh trên';
      case this.SNAP_POSITIONS.ALIGN_BOTTOM_EDGE:
        return 'Thẳng cạnh dưới';
      case this.SNAP_POSITIONS.CENTER_ALIGN:
        return 'Căn giữa tâm';
      default:
        return '';
    }
  }

  // Cập nhật createSnapLines để hiển thị lines dựa trên snap type mới
  createSnapLines(snapInfo) {
    this.clearSnapLines();

    if (!snapInfo || !snapInfo.target) return;

    const lines = [];
    const draggedBox = this.getUpdatedBoundingBox(this.selectedModel);
    const targetBox = this.getUpdatedBoundingBox(snapInfo.target);

    if (!draggedBox || !targetBox) return;

    // Tạo lines dựa trên loại snap mới
    switch (snapInfo.type) {
      case this.SNAP_POSITIONS.ADJACENT_LEFT:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.max.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ADJACENT_RIGHT:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.max.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ADJACENT_FRONT:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.max.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ADJACENT_BACK:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.max.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ADJACENT_TOP:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.max.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ADJACENT_BOTTOM:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.max.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.CENTER_ALIGN:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x + (draggedBox.max.x - draggedBox.min.x) / 2,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(
              targetBox.min.x + (targetBox.max.x - targetBox.min.x) / 2,
              targetBox.min.y,
              targetBox.min.z
            )
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_LEFT_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_RIGHT_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.max.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.max.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_FRONT_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_BACK_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.max.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.max.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_TOP_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.max.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.max.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.ALIGN_BOTTOM_EDGE:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;
    }

    this._snapLines = lines;
  }

  // Method để lấy thông tin chi tiết về mặt tiếp xúc (cho debug/UI)
  getDetailedTouchingInfo(model1, model2) {
    const touchingInfo = this.detectTouchingFaces(model1, model2);

    if (!touchingInfo.isTouching) {
      return null;
    }

    return {
      isTouching: true,
      model1Face: touchingInfo.model1Face,
      model2Face: touchingInfo.model2Face,
      direction: touchingInfo.direction,
      gap: touchingInfo.gap,
      model1FaceDescription: this.getFaceDescription(touchingInfo.model1Face),
      model2FaceDescription: this.getFaceDescription(touchingInfo.model2Face),
      directionDescription: this.getDirectionDescription(
        touchingInfo.direction
      ),
    };
  }

  // Helper methods cho descriptions
  getFaceDescription(face) {
    switch (face) {
      case this.BOX_FACES.LEFT:
        return 'Mặt trái';
      case this.BOX_FACES.RIGHT:
        return 'Mặt phải';
      case this.BOX_FACES.FRONT:
        return 'Mặt trước';
      case this.BOX_FACES.BACK:
        return 'Mặt sau';
      case this.BOX_FACES.TOP:
        return 'Mặt trên';
      case this.BOX_FACES.BOTTOM:
        return 'Mặt dưới';
      default:
        return 'Không xác định';
    }
  }

  getDirectionDescription(direction) {
    switch (direction) {
      case 'x':
        return 'Tiếp xúc theo trục X (trái/phải)';
      case 'y':
        return 'Tiếp xúc theo trục Y (trên/dưới)';
      case 'z':
        return 'Tiếp xúc theo trục Z (trước/sau)';
      default:
        return 'Không xác định';
    }
  }

  // Method để visualize touching faces (debug)
  visualizeTouchingFaces(model1, model2, color1 = 0xff0000, color2 = 0x00ff00) {
    const touchingInfo = this.detectTouchingFaces(model1, model2);

    if (!touchingInfo.isTouching) {
      console.log('Models are not touching');
      return null;
    }

    console.log(`Touching detected:
      Model1 ${touchingInfo.model1Face} face touching Model2 ${touchingInfo.model2Face} face
      Direction: ${touchingInfo.direction}
      Gap: ${touchingInfo.gap}`);

    // Có thể thêm visual indicators ở đây
    const wireframe1 = this.visualizeOBB(model1, color1);
    const wireframe2 = this.visualizeOBB(model2, color2);

    return { wireframe1, wireframe2, touchingInfo };
  }

  // Tìm vị trí snap gần nhất với vị trí hiện tại (chỉ với module khuyết cột đang tiếp xúc với cột)
  findNearestColumnSnapPosition(draggedModel, currentPosition) {
    if (!this._snapEnabled) return null;
    const snapTargets = this.findColumnSnapTargets(draggedModel);
    // Nếu không có model nào đang tiếp xúc, không có snap
    if (snapTargets.length === 0) return null;
    let nearestSnap = null;
    const minDistance = this._snapTolerance;
    for (const target of snapTargets) {
      const snapPositions = this.calculateColumnSnapPositions(
        draggedModel,
        target
      );
      for (const snap of snapPositions) {
        nearestSnap = snap;
      }
    }
    return nearestSnap;
  }

  // Tìm vị trí snap gần nhất với vị trí hiện tại (chỉ với models đang tiếp xúc)
  findNearestSnapPosition(draggedModel, currentPosition) {
    if (!this._snapEnabled) return null;
    const snapTargets = this.findSnapTargets(draggedModel);
    // Nếu không có model nào đang tiếp xúc, không có snap
    if (snapTargets.length === 0) return null;
    let nearestSnap = null;
    let minDistance = this._snapTolerance;
    for (const target of snapTargets) {
      const snapPositions = this.calculateSnapPositions(draggedModel, target);
      for (const snap of snapPositions) {
        const distance = currentPosition.distanceTo(snap.position);
        if (distance < minDistance) {
          // Kiểm tra xem vị trí snap có hợp lệ không (không va chạm)
          if (this.isSnapPositionValid(draggedModel, snap.position, target)) {
            minDistance = distance;
            nearestSnap = snap;
          }
        }
      }
    }
    return nearestSnap;
  }

  // Kiểm tra vị trí snap có hợp lệ không
  isSnapPositionValid(draggedModel, snapPosition, excludeTarget) {
    const tempPos = draggedModel.position.clone();
    draggedModel.position.copy(snapPosition);
    draggedModel.updateMatrixWorld(true);

    let isValid = true;

    // Kiểm tra va chạm với building elements
    if (this.checkBuildingCollision(draggedModel)) {
      isValid = false;
    }

    // Kiểm tra va chạm với các model khác (trừ target model)
    if (isValid) {
      const allModels = this.scene.children.filter(
        child =>
          child.userData?.type === 'module' &&
          // child.userData?.requireCollision === true &&
          child !== draggedModel &&
          child !== excludeTarget
      );

      for (const model of allModels) {
        if (this.checkCollision(draggedModel, model)) {
          isValid = false;
          break;
        }
      }
    }

    // Khôi phục vị trí
    draggedModel.position.copy(tempPos);
    draggedModel.updateMatrixWorld(true);

    return isValid;
  }

  // Tạo visual snap lines
  createSnapLines(snapInfo) {
    this.clearSnapLines();

    if (!snapInfo || !snapInfo.target) return;

    const lines = [];
    const draggedBox = this.getUpdatedBoundingBox(this.selectedModel);
    const targetBox = this.getUpdatedBoundingBox(snapInfo.target);

    if (!draggedBox || !targetBox) return;

    // Tạo lines dựa trên loại snap
    switch (snapInfo.type) {
      case this.SNAP_POSITIONS.LEFT_ALIGN:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.max.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.RIGHT_ALIGN:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.max.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.CENTER_ALIGN:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x + (draggedBox.max.x - draggedBox.min.x) / 2,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(
              targetBox.min.x + (targetBox.max.x - targetBox.min.x) / 2,
              targetBox.min.y,
              targetBox.min.z
            )
          )
        );
        break;

      case this.SNAP_POSITIONS.EDGE_LEFT:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.min.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.min.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;

      case this.SNAP_POSITIONS.EDGE_RIGHT:
        lines.push(
          this.createSnapLine(
            new THREE.Vector3(
              draggedBox.max.x,
              draggedBox.min.y,
              draggedBox.min.z
            ),
            new THREE.Vector3(targetBox.max.x, targetBox.min.y, targetBox.min.z)
          )
        );
        break;
    }

    this._snapLines = lines;
  }

  // Tạo một snap line
  createSnapLine(start, end) {
    const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
    const line = new THREE.Line(geometry, this._snapLinesMaterial);
    line.scale.set(10, 10, 10);
    line.userData = { isSnapLine: true };
    this.scene.add(line);
    return line;
  }

  // Xóa tất cả snap lines
  clearSnapLines() {
    this._snapLines.forEach(line => {
      this.scene.remove(line);
      line.geometry.dispose();
    });
    this._snapLines = [];
  }

  // Callback để thông báo snap indicator cho UI
  setSnapIndicatorCallback(callback) {
    this._snapIndicatorCallback = callback;
  }

  // Hiển thị snap indicator
  showSnapIndicator(snapInfo) {
    if (this._snapIndicatorCallback) {
      this._snapIndicatorCallback(snapInfo.description, true);
    }
  }

  // Ẩn snap indicator
  hideSnapIndicator() {
    if (this._snapIndicatorCallback) {
      this._snapIndicatorCallback('', false);
    }
  }

  // Enable/disable snap mode
  setSnapEnabled(enabled) {
    this._snapEnabled = enabled;
    if (!enabled) {
      this.clearSnapLines();
      this.hideSnapIndicator();
      this._currentSnapTarget = null;
      this._currentSnapPosition = null;
    }
  }

  // Set snap tolerance
  setSnapTolerance(tolerance) {
    this._snapTolerance = tolerance;
  }

  // Set adjacent threshold
  setAdjacentThreshold(threshold) {
    this._adjacentThreshold = threshold;
  }

  initEvents() {
    this.canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
  }

  /**
   * Tạm dừng việc chọn/bỏ chọn module khi bấm lên canvas. Công cụ đo DIM thủ công
   * và ghi chú dùng click để đặt điểm trên mô hình, không được chọn module (hiện
   * tay nắm di chuyển đè lên điểm vừa đặt). Không ảnh hưởng OrbitControls.
   */
  setSelectionSuspended(suspended) {
    this._selectionSuspended = !!suspended;
  }

  //Ẩn nút di chuyển module khi click vào inside
  onPointerDown(event) {
    if (this._selectionSuspended) return;
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(
      // this.scene.children,
      this.scene.children.filter(obj => obj.visibleForRaycast !== false),
      true
    );

    if (intersects.length > 0) {
      if (
        (intersects[0].object.userData?.isInside === true ||
          (intersects[0].object.userData?.type &&
            intersects[0].object.userData?.type !== InsideType.COL_PANEL)) &&
        this.sceneManager.cabinetType !== 'kitchen'
      )
        return;

      const mesh = intersects[0].object;
      const model = this.findTopLevelParent(mesh);

      if (model.userData?.type === 'module') {
        this.selectedModel = model;
        this._lastValidPosition = model.position.clone();

        // const box = new THREE.Box3().setFromObject(model);
        // const boxHelper = new THREE.Box3Helper(box, 0x00ff00);
        // this.scene.add(boxHelper);

        const screenPos = {
          x: event.clientX - this._btnWidth,
          y: event.clientY - this._btnWidth,
        };
        this._onUISelect?.(model, screenPos, event);
      } else {
        this._onUIDeselect?.();
      }
    } else {
      this._onUIDeselect?.();
    }
  }

  findTopLevelParent(object) {
    let current = object;
    while (current.parent && current.parent.type !== 'Scene') {
      current = current.parent;
    }
    return current;
  }

  // Cập nhật phương thức getOrientedBoundingBox để chính xác hơn
  getOrientedBoundingBox(object) {
    // Đảm bảo world matrix được update
    object.updateMatrixWorld(true);

    // Lưu lại transform hiện tại của object
    const originalPosition = object.position.clone();
    const originalRotation = object.rotation.clone();
    const originalScale = object.scale.clone();
    const originalMatrix = object.matrix.clone();

    // Tạm thời reset transform của object về identity để lấy local size
    object.position.set(0, 0, 0);
    object.rotation.set(0, 0, 0);
    object.scale.set(1, 1, 1);
    object.updateMatrixWorld(true);

    // Tạo bounding box trong local space (không bị ảnh hưởng bởi rotation)
    const localBox = new THREE.Box3().setFromObject(object);

    // Lấy kích thước thực trong local space
    const size = localBox.getSize(new THREE.Vector3());
    const localCenter = localBox.getCenter(new THREE.Vector3());

    // Khôi phục lại transform ban đầu
    object.position.copy(originalPosition);
    object.rotation.copy(originalRotation);
    object.scale.copy(originalScale);
    object.updateMatrixWorld(true);

    // Bây giờ tạo bounding box với world transform để lấy center và corners đúng
    const worldBox = new THREE.Box3().setFromObject(object);
    const worldCenter = worldBox.getCenter(new THREE.Vector3());

    // Tạo BoxGeometry từ kích thước thực trong local space
    const boxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);

    // Lấy world matrix của object
    const worldMatrix = object.matrixWorld;

    // Tạo 8 góc của bounding box từ local size, nhưng centered tại local center
    const halfSize = size.clone().multiplyScalar(0.5);
    const corners = [
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z + halfSize.z
      ),
    ];

    // Transform tất cả các góc theo world matrix
    corners.forEach(corner => corner.applyMatrix4(worldMatrix));

    // Tính toán các trục orientation từ rotation matrix
    const rotationMatrix = new THREE.Matrix4().extractRotation(worldMatrix);
    const axes = [
      new THREE.Vector3(1, 0, 0).applyMatrix4(rotationMatrix),
      new THREE.Vector3(0, 1, 0).applyMatrix4(rotationMatrix),
      new THREE.Vector3(0, 0, 1).applyMatrix4(rotationMatrix),
    ];

    return {
      corners: corners,
      center: object.getWorldPosition(new THREE.Vector3()),
      axes: axes,
      rotation: object.rotation.clone(),
      scale: object.scale.clone(),
      worldMatrix: worldMatrix.clone(),
      geometry: boxGeometry,
      size: size, // Kích thước thực trong local space
    };
  }

  // // Cải thiện phương thức checkOBBCollision
  // checkOBBCollision(object1, object2) {
  //   const obb1 = this.getOrientedBoundingBox(object1);
  //   const obb2 = this.getOrientedBoundingBox(object2);

  //   if (!obb1 || !obb2) return false;

  //   // Sử dụng Separating Axis Theorem (SAT)
  //   // Kiểm tra tất cả các trục có thể tách biệt hai OBB
  //   const allAxes = [...obb1.axes, ...obb2.axes];

  //   // Thêm các trục cross product giữa các cạnh của hai OBB
  //   for (let i = 0; i < 3; i++) {
  //     for (let j = 0; j < 3; j++) {
  //       const crossAxis = new THREE.Vector3().crossVectors(
  //         obb1.axes[i],
  //         obb2.axes[j]
  //       );
  //       if (crossAxis.lengthSq() > 0.0001) {
  //         // Tránh trục có độ dài 0
  //         crossAxis.normalize();
  //         allAxes.push(crossAxis);
  //       }
  //     }
  //   }

  //   // Test separation trên mỗi trục
  //   for (const axis of allAxes) {
  //     if (this.isSeparatedOnAxis(obb1.corners, obb2.corners, axis)) {
  //       return false; // Tách biệt trên trục này, không có va chạm
  //     }
  //   }

  //   return true; // Không tìm thấy trục tách biệt, có va chạm
  // }

  checkOBBCollision(object1, object2) {
    const obbs1 = this.getMultiOBB(object1);
    const obbs2 = this.getMultiOBB(object2);

    if (!obbs1.length || !obbs2.length) return false;

    for (const obb1 of obbs1) {
      for (const obb2 of obbs2) {
        if (this.testOBBvsOBB(obb1, obb2)) return true;
      }
    }

    return false;
  }

  // Build OBB cho từng mesh con
  getMultiOBB(moduleGroup) {
    if (moduleGroup.userData.collisionOBBs) {
      return moduleGroup.userData.collisionOBBs;
    }

    const obbs = [];

    moduleGroup.traverse(child => {
      if (!child.isMesh) return;
      if (child.userData.notCollision) return;
      if (child.userData.notNesting) return;

      if (child.scale.x === 0 || child.scale.y === 0 || child.scale.y === 0) {
        return;
      }

      // Lọc lại chỉ lấy các mesh khung tủ và cột, dầm
      const validNames = [
        'BIA',
        'DAY',
        'NOC',
        'HAU',
        'CHAN',
        'DOTKE',
        'COLUMN',
        'BEAM',
        'COUNTERTOP',
        'BACKSPLASH',
        'VACH',
        'DAU-GIUONG',
        'THANH-GIUONG',
      ];
      const isValid = validNames.some(keyword => child.name.includes(keyword));
      if (!isValid) return;

      child.updateMatrixWorld(true);

      // Lấy local AABB của geometry (chưa có transform)
      const localBox = new THREE.Box3().setFromBufferAttribute(
        child.geometry.attributes.position
      );

      const center = new THREE.Vector3();
      const halfSize = new THREE.Vector3();
      localBox.getCenter(center);
      localBox.getSize(halfSize);
      halfSize.multiplyScalar(0.5);

      // Bỏ qua mesh quá nhỏ
      if (halfSize.x < 0.001 || halfSize.y < 0.001 || halfSize.z < 0.001)
        return;

      // Lấy world matrix của mesh con
      const matrix = child.matrixWorld.clone();

      // Extract rotation axes từ world matrix (đã bao gồm cả rotation của parent)
      const axes = [
        new THREE.Vector3(
          matrix.elements[0],
          matrix.elements[1],
          matrix.elements[2]
        ).normalize(),
        new THREE.Vector3(
          matrix.elements[4],
          matrix.elements[5],
          matrix.elements[6]
        ).normalize(),
        new THREE.Vector3(
          matrix.elements[8],
          matrix.elements[9],
          matrix.elements[10]
        ).normalize(),
      ];

      // World center = apply full matrixWorld lên local center
      const worldCenter = center.clone().applyMatrix4(matrix);

      // World halfSize = scale theo matrixWorld
      const scale = new THREE.Vector3();
      matrix.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale);
      const worldHalfSize = halfSize.clone().multiply(scale);

      obbs.push({ center: worldCenter, halfSize: worldHalfSize, axes });
    });

    moduleGroup.userData.collisionOBBs = obbs;
    return obbs;
  }

  // SAT test giữa 2 OBB
  testOBBvsOBB(obb1, obb2) {
    const allAxes = [...obb1.axes, ...obb2.axes];

    // Thêm cross product axes
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        const cross = new THREE.Vector3().crossVectors(
          obb1.axes[i],
          obb2.axes[j]
        );
        if (cross.lengthSq() > 0.0001) {
          allAxes.push(cross.normalize());
        }
      }
    }

    for (const axis of allAxes) {
      const p1 = this.projectOBB(obb1, axis);
      const p2 = this.projectOBB(obb2, axis);

      // Nếu projection không overlap → tách biệt → không collision
      if (p1.max < p2.min || p2.max < p1.min) return false;
    }

    return true;
  }

  // Project OBB lên 1 trục
  projectOBB(obb, axis) {
    const centerProj = obb.center.dot(axis);
    const radius =
      Math.abs(obb.axes[0].dot(axis)) * obb.halfSize.x +
      Math.abs(obb.axes[1].dot(axis)) * obb.halfSize.y +
      Math.abs(obb.axes[2].dot(axis)) * obb.halfSize.z;

    return { min: centerProj - radius, max: centerProj + radius };
  }

  // // Gọi khi module thay đổi transform
  // invalidateCollisionCache(moduleGroup) {
  //   moduleGroup.userData.collisionOBBs = null;
  // }

  // //------ Cách xử lý collision mới đúng cả tủ có hình dạng phức tạp ------
  // checkOBBCollision(object1, object2) {
  //   const boxes1 = this.getMultiAABB(object1);
  //   const boxes2 = this.getMultiAABB(object2);

  //   if (!boxes1.length || !boxes2.length) return false;

  //   for (const boxA of boxes1) {
  //     for (const boxB of boxes2) {
  //       if (boxA.intersectsBox(boxB)) return true;
  //     }
  //   }

  //   return false;
  // }

  // getMultiAABB(moduleGroup) {
  //   // Dùng cache nếu đã build
  //   if (moduleGroup.userData.collisionBoxes) {
  //     return moduleGroup.userData.collisionBoxes;
  //   }

  //   const boxes = [];

  //   moduleGroup.traverse(child => {
  //     if (!child.isMesh) return;
  //     if (child.userData.notCollision) return;
  //     if (child.userData.notNesting) return; // skip mesh phụ như lam, frame...

  //     child.updateMatrixWorld(true);
  //     const box = new THREE.Box3().setFromObject(child);

  //     // Bỏ qua mesh quá nhỏ (tránh noise)
  //     const size = new THREE.Vector3();
  //     box.getSize(size);
  //     if (size.x < 0.001 || size.y < 0.001 || size.z < 0.001) return;

  //     boxes.push(box);
  //   });

  //   moduleGroup.userData.collisionBoxes = boxes;
  //   return boxes;
  // }

  // // Gọi khi module thay đổi vị trí/scale để rebuild cache
  // invalidateCollisionCache(moduleGroup) {
  //   moduleGroup.userData.collisionBoxes = null;
  // }
  // //------ Cách xử lý collision mới đúng cả tủ có hình dạng phức tạp ------

  isSeparatedOnAxis(corners1, corners2, axis) {
    // Project all corners onto the axis
    const projections1 = corners1.map(corner => corner.dot(axis));
    const projections2 = corners2.map(corner => corner.dot(axis));

    const min1 = Math.min(...projections1);
    const max1 = Math.max(...projections1);
    const min2 = Math.min(...projections2);
    const max2 = Math.max(...projections2);

    // Check if projections are separated
    return max1 < min2 || max2 < min1;
  }

  // Phương pháp 3: Sử dụng sphere collision (đơn giản hơn nhưng kém chính xác)
  checkSphereCollision(object1, object2) {
    const sphere1 = new THREE.Sphere();
    const sphere2 = new THREE.Sphere();

    // Tính bounding sphere cho mỗi object
    const box1 = new THREE.Box3().setFromObject(object1);
    const box2 = new THREE.Box3().setFromObject(object2);

    box1.getBoundingSphere(sphere1);
    box2.getBoundingSphere(sphere2);

    return sphere1.intersectsSphere(sphere2);
  }

  // Phương pháp 4: Cập nhật bounding box sau khi xoay
  getUpdatedBoundingBox(object) {
    // Force update world matrix
    object.updateMatrixWorld(true);

    // Tạo bounding box mới
    const box = new THREE.Box3().setFromObject(object);
    return box;
  }

  // // Hàm cũ
  // checkCollision(model1, model2) {
  //   //Bản gốc
  //   if (!model1 || !model2 || model1 === model2) return false;

  //   // Đảm bảo world matrix được update
  //   model1.updateMatrixWorld(true);
  //   model2.updateMatrixWorld(true);

  //   // Luôn sử dụng OBB collision nếu có rotation
  //   if (this.hasRotation(model1) || this.hasRotation(model2)) {
  //     return this.checkOBBCollision(model1, model2);
  //   }

  //   // Nếu không có rotation, sử dụng AABB đơn giản
  //   const box1 = new THREE.Box3().setFromObject(model1);
  //   const box2 = new THREE.Box3().setFromObject(model2);

  //   return box1.intersectsBox(box2);
  // }

  checkCollision(model1, model2) {
    if (!model1 || !model2 || model1 === model2) return false;

    //Tạm scale nhỏ hơn để dễ di chuyển qua các khe hở nhỏ
    const cloneModel1 = model1.clone();
    const cloneModel2 = model2.clone();

    cloneModel1.scale.set(0.999, 0.999, 0.999);
    cloneModel2.scale.set(0.999, 0.999, 0.999);

    // Đảm bảo world matrix được update
    cloneModel1.updateMatrixWorld(true);
    cloneModel2.updateMatrixWorld(true);

    return this.checkOBBCollision(cloneModel1, cloneModel2);
  }

  // // Cải thiện phương thức hasRotation để nhạy hơn
  // hasRotation(object) {
  //   const rotation = object.rotation;
  //   const threshold = 0.001; // Threshold nhỏ hơn để detect rotation chính xác hơn
  //   return (
  //     Math.abs(rotation.x) > threshold ||
  //     Math.abs(rotation.y) > threshold ||
  //     Math.abs(rotation.z) > threshold
  //   );
  // }

  // Kiểm tra va chạm với tường
  checkWallCollision(model) {
    if (!this.sceneManager.walls || this.sceneManager.walls.length === 0)
      return false;

    for (const wall of this.sceneManager.walls) {
      if (this.checkCollision(model, wall.mesh)) {
        return true;
      }
    }
    return false;
  }

  // Kiểm tra va chạm với cột
  checkColumnCollision(model) {
    if (!this.sceneManager.columns || this.sceneManager.columns.length === 0)
      return false;

    for (const column of this.sceneManager.columns) {
      if (this.checkCollision(model, column.mesh)) {
        return true;
      }
    }
    return false;
  }

  // Kiểm tra va chạm với dầm
  checkBeamCollision(model) {
    if (!this.sceneManager.beams || this.sceneManager.beams.length === 0)
      return false;

    for (const beam of this.sceneManager.beams) {
      if (this.checkCollision(model, beam.mesh)) {
        return true;
      }
    }
    return false;
  }

  // Lấy mức độ cao của sàn
  getFloorLevel() {
    if (!this.sceneManager.floor) return 0;

    const floorBox = this.getUpdatedBoundingBox(this.sceneManager.floor);
    return floorBox.max.y; // Trả về mức cao nhất của sàn
  }

  // Kiểm tra model có nằm trong phạm vi sàn không (cập nhật cho rotation)
  checkFloorBounds(model) {
    if (!this.sceneManager.floor) return true;

    // Nếu model bị xoay, sử dụng bounding box cập nhật
    const modelBox = this.getUpdatedBoundingBox(model);
    const floorBox = this.getUpdatedBoundingBox(this.sceneManager.floor);

    // Kiểm tra model có nằm hoàn toàn trong phạm vi sàn không
    const modelMin = modelBox.min;
    const modelMax = modelBox.max;
    const floorMin = floorBox.min;
    const floorMax = floorBox.max;

    return (
      modelMin.x >= floorMin.x &&
      modelMax.x <= floorMax.x &&
      modelMin.z >= floorMin.z &&
      modelMax.z <= floorMax.z
    );
  }

  // Kiểm tra model có nằm trong phạm vi trần nhà không (cập nhật cho rotation)
  checkCeilingBounds(model) {
    if (!this.sceneManager.ceiling) return true;

    const modelBox = this.getUpdatedBoundingBox(model);

    const cloneCeiling = this.sceneManager.ceiling.clone();
    cloneCeiling.scale.setY(0.95); //trừ hao chiều cao trần
    // const ceilingBox = this.getUpdatedBoundingBox(this.sceneManager.ceiling);
    const ceilingBox = this.getUpdatedBoundingBox(cloneCeiling);

    const modelMax = modelBox.max;
    const ceilingMin = ceilingBox.min;
    const ceilingMax = ceilingBox.max;

    // Model phải nằm hoàn toàn bên dưới trần (theo trục Y)
    // và trong phạm vi X/Z của trần
    return (
      modelMax.y <= ceilingMin.y &&
      modelBox.min.x >= ceilingMin.x &&
      modelMax.x <= ceilingMax.x &&
      modelBox.min.z >= ceilingMin.z &&
      modelMax.z <= ceilingMax.z
    );
  }

  // Kiểm tra tất cả va chạm với tường, cột, dầm và phạm vi sàn
  checkBuildingCollision(model) {
    return (
      this.checkWallCollision(model) ||
      this.checkColumnCollision(model) ||
      this.checkBeamCollision(model) ||
      !this.checkFloorBounds(model) ||
      !this.checkCeilingBounds(model)
    );
  }

  // Tìm vị trí trượt hợp lệ khi va chạm với tất cả các model và tường
  findSlidingPosition(draggedModel, targetPosition, originalPosition) {
    // Lấy tất cả các model khác trong scene
    const allModels = this.scene.children.filter(
      // child => child.userData?.type === 'module' && child !== draggedModel
      child =>
        child.userData?.requireCollision === true && child !== draggedModel
    );

    // Tạm thời đặt model ở vị trí mục tiêu để kiểm tra va chạm
    const tempPosition = draggedModel.position.clone();
    draggedModel.position.copy(targetPosition);

    // Update world matrix để collision detection chính xác
    draggedModel.updateMatrixWorld(true);

    const collidingModels = [];
    let hasWallCollision = false;

    // Kiểm tra va chạm với các model khác
    for (const model of allModels) {
      if (this.checkCollision(draggedModel, model)) {
        collidingModels.push(model);
      }
    }

    // Kiểm tra va chạm với tường, cột, dầm và phạm vi sàn
    if (this.checkBuildingCollision(draggedModel)) {
      hasWallCollision = true;
    }

    // Khôi phục vị trí tạm thời
    draggedModel.position.copy(tempPosition);
    draggedModel.updateMatrixWorld(true);

    if (collidingModels.length === 0 && !hasWallCollision) {
      return targetPosition; // Không có va chạm, vị trí hợp lệ
    }

    // Có va chạm, tìm vị trí trượt
    const slidingPosition = this.calculateSlidingPositionMultiple(
      draggedModel,
      collidingModels,
      targetPosition,
      originalPosition,
      hasWallCollision
    );

    return slidingPosition;
  }

  calculateSlidingPositionMultiple(
    draggedModel,
    collidingModels,
    targetPosition,
    originalPosition,
    hasWallCollision = false
  ) {
    // Tính toán vector di chuyển
    const moveVector = new THREE.Vector3().subVectors(
      targetPosition,
      originalPosition
    );

    // Tính toán các trục chính để trượt
    const axes = [
      new THREE.Vector3(1, 0, 0), // X axis
      new THREE.Vector3(0, 0, 1), // Z axis
      new THREE.Vector3(0, 1, 0), // Y axis
    ];

    let bestPosition = originalPosition.clone();
    let maxDistance = 0;

    // // Thử trượt theo từng trục
    // for (const axis of axes) {
    //   // Tính toán component của vector di chuyển theo trục này
    //   const axisComponent = axis.clone().multiplyScalar(moveVector.dot(axis));

    //   if (axisComponent.length() < 0.001) continue; // Bỏ qua nếu component quá nhỏ

    //   // Thử di chuyển theo trục này với các bước nhỏ
    //   const steps = 1;
    //   for (let i = 1; i <= steps; i++) {
    //     const testPosition = originalPosition
    //       .clone()
    //       .add(axisComponent.clone().multiplyScalar(i / steps));

    //     // Kiểm tra va chạm với TẤT CẢ các model và tường
    //     if (
    //       this.isPositionValidForAll(
    //         draggedModel,
    //         testPosition,
    //         collidingModels
    //       )
    //     ) {
    //       const distance = testPosition.distanceTo(originalPosition);
    //       if (distance > maxDistance) {
    //         maxDistance = distance;
    //         bestPosition = testPosition.clone();
    //       }
    //     } else {
    //       break; // Dừng khi gặp va chạm
    //     }
    //   }
    // }

    // Thử trượt theo từng trục
    for (const axis of axes) {
      const axisComponent = axis.clone().multiplyScalar(moveVector.dot(axis));
      if (axisComponent.length() < 0.001) continue;

      const testPosition = originalPosition.clone().add(axisComponent);

      if (
        this.isPositionValidForAll(draggedModel, testPosition, collidingModels)
      ) {
        const distance = testPosition.distanceTo(originalPosition);
        if (distance > maxDistance) {
          maxDistance = distance;
          bestPosition = testPosition.clone();
        }
      }
    }

    // Thử trượt theo hướng kết hợp (diagonal sliding)
    if (Math.abs(moveVector.x) > 0.001 && Math.abs(moveVector.z) > 0.001) {
      // Thử trượt chỉ theo X
      const xOnlyMove = new THREE.Vector3(moveVector.x, 0, 0);
      const xPosition = originalPosition.clone().add(xOnlyMove);

      if (
        this.isPositionValidForAll(draggedModel, xPosition, collidingModels)
      ) {
        const distance = xPosition.distanceTo(originalPosition);
        if (distance > maxDistance) {
          maxDistance = distance;
          bestPosition = xPosition.clone();
        }
      }

      // Thử trượt chỉ theo Z
      const zOnlyMove = new THREE.Vector3(0, 0, moveVector.z);
      const zPosition = originalPosition.clone().add(zOnlyMove);

      if (
        this.isPositionValidForAll(draggedModel, zPosition, collidingModels)
      ) {
        const distance = zPosition.distanceTo(originalPosition);
        if (distance > maxDistance) {
          maxDistance = distance;
          bestPosition = zPosition.clone();
        }
      }
    }

    return bestPosition;
  }

  // Kiểm tra xem vị trí có hợp lệ với tất cả các model và tường trong scene không
  isPositionValidForAll(draggedModel, testPosition, excludeModels = []) {
    const tempPos = draggedModel.position.clone();
    draggedModel.position.copy(testPosition);

    // Update world matrix để collision detection chính xác
    draggedModel.updateMatrixWorld(true);

    let isValid = true;

    // Kiểm tra va chạm với tường, cột, dầm và phạm vi sàn
    if (this.checkBuildingCollision(draggedModel)) {
      isValid = false;
    }

    // Nếu vẫn hợp lệ, kiểm tra va chạm với các model khác
    if (isValid) {
      const allModels = this.scene.children.filter(
        // child => child.userData?.type === 'module' && child !== draggedModel
        child =>
          child.userData?.requireCollision === true && child !== draggedModel
      );

      for (const model of allModels) {
        if (this.checkCollision(draggedModel, model)) {
          isValid = false;
          break;
        }
      }
    }

    // Khôi phục vị trí
    draggedModel.position.copy(tempPos);
    draggedModel.updateMatrixWorld(true);

    return isValid;
  }

  // Cập nhật method checkCollisionWithSlidingPosition với snap positioning
  checkCollisionWithSlidingPosition(draggedModel, newPosition) {
    const originalPosition = draggedModel.position.clone();

    // Kiểm tra snap position trước
    if (this._snapEnabled) {
      // let nearestSnap = null;
      // if (draggedModel.userData?.isSpecial) {
      //   nearestSnap = this.findNearestColumnSnapPosition(
      //     this.selectedModel,
      //     newPosition
      //   );
      // } else {
      //   nearestSnap = this.findNearestSnapPosition(
      //     this.selectedModel,
      //     newPosition
      //   );
      // }

      const nearestSnap = this.findNearestSnapPosition(
        draggedModel,
        newPosition
      );

      if (nearestSnap) {
        if (
          this._currentSnapTarget !== nearestSnap.target ||
          this._currentSnapPosition !== nearestSnap.type
        ) {
          // Clear old snap lines and create new ones
          this.clearSnapLines();
          this.createSnapLines(nearestSnap);
          this.showSnapIndicator(nearestSnap);

          this._currentSnapTarget = nearestSnap.target;
          this._currentSnapPosition = nearestSnap.type;
        }

        // Return snap position instead of original target position
        return nearestSnap.position;
      } else {
        // Clear snap indicators if no snap found
        if (this._currentSnapTarget) {
          this.clearSnapLines();
          this.hideSnapIndicator();
          this._currentSnapTarget = null;
          this._currentSnapPosition = null;
        }
      }
    }

    // Fallback to normal sliding position calculation
    const slidingPosition = this.findSlidingPosition(
      draggedModel,
      newPosition,
      originalPosition
    );

    return slidingPosition;
  }

  // Thêm phương thức để update collision detection khi model rotation thay đổi
  updateModelRotation(model, newRotation) {
    if (!model) return false;

    // Backup vị trí và rotation hiện tại
    const currentPosition = model.position.clone();
    const currentRotation = {
      x: model.rotation.x,
      y: model.rotation.y,
      z: model.rotation.z,
    };

    // Apply rotation mới
    if (typeof newRotation === 'number') {
      // Nếu newRotation là số (degree), apply vào trục Y
      model.rotation.y = (newRotation * Math.PI) / 180;
    } else if (newRotation && typeof newRotation === 'object') {
      // Nếu newRotation là object có x, y, z
      if (newRotation.x !== undefined) model.rotation.x = newRotation.x;
      if (newRotation.y !== undefined) model.rotation.y = newRotation.y;
      if (newRotation.z !== undefined) model.rotation.z = newRotation.z;
    }

    // Update world matrix
    model.updateMatrixWorld(true);

    // Kiểm tra va chạm sau khi rotation với building elements
    if (this.checkBuildingCollision(model)) {
      // Nếu có va chạm, rollback rotation
      model.rotation.x = currentRotation.x;
      model.rotation.y = currentRotation.y;
      model.rotation.z = currentRotation.z;
      model.updateMatrixWorld(true);
      return false; // Rotation không hợp lệ
    }

    // Kiểm tra va chạm với các model khác
    const allModels = this.scene.children.filter(
      // child => child.userData?.type === 'module' && child !== model
      child => child.userData?.requireCollision === true && child !== model
    );

    for (const otherModel of allModels) {
      if (this.checkCollision(model, otherModel)) {
        // Nếu có va chạm, rollback rotation
        model.rotation.x = currentRotation.x;
        model.rotation.y = currentRotation.y;
        model.rotation.z = currentRotation.z;
        model.updateMatrixWorld(true);
        return false; // Rotation không hợp lệ
      }
    }

    // Update last valid position
    this._lastValidPosition = currentPosition.clone();
    return true; // Rotation hợp lệ
  }

  // Thêm method để set rotation đơn giản
  setModelRotation(model, rotationY) {
    if (!model) return false;

    return this.updateModelRotation(model, {
      x: model.rotation.x,
      y: rotationY,
      z: model.rotation.z,
    });
  }

  // Thêm method để get current rotation
  getModelRotation(model) {
    if (!model) return 0;

    // Convert radian to degree
    return (model.rotation.y * 180) / Math.PI;
  }

  // Thêm phương thức để visualize OBB (dùng để debug)
  visualizeOBB(object, color = 0xff0000) {
    const obb = this.getOrientedBoundingBox(object);
    if (!obb) return null;

    // Tạo geometry cho OBB visualization
    const geometry = new THREE.BufferGeometry();
    const vertices = [];

    // Tạo 12 edges của box từ 8 corners
    const edges = [
      [0, 1],
      [1, 3],
      [3, 2],
      [2, 0], // Bottom face
      [4, 5],
      [5, 7],
      [7, 6],
      [6, 4], // Top face
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7], // Vertical edges
    ];

    edges.forEach(([i, j]) => {
      vertices.push(...obb.corners[i].toArray());
      vertices.push(...obb.corners[j].toArray());
    });

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );

    const material = new THREE.LineBasicMaterial({ color: color });
    const wireframe = new THREE.LineSegments(geometry, material);
    wireframe.userData = { isOBBVisualization: true };

    return wireframe;
  }

  // Method để kiểm tra model có đang tiếp xúc với model nào không
  getTouchingModels(targetModel) {
    if (!targetModel) return [];

    const touchingModels = [];
    const allModels = this.scene.children.filter(
      // child => child.userData?.type === 'module' && child !== targetModel
      child =>
        child.userData?.requireCollision === true && child !== targetModel
    );

    for (const model of allModels) {
      if (this.areModelsAdjacent(targetModel, model)) {
        const touchingInfo = this.areModelsExactlyTouching(targetModel, model);
        touchingModels.push({
          model: model,
          adjacent: true,
          exactlyTouching: touchingInfo.isTouching,
          touchingFace: touchingInfo.touchingFace,
          direction: touchingInfo.direction,
        });
      }
    }

    return touchingModels;
  }

  // Getter/Setter cho snap mode
  isSnapEnabled() {
    return this._snapEnabled;
  }

  getSnapTolerance() {
    return this._snapTolerance;
  }

  getAdjacentThreshold() {
    return this._adjacentThreshold;
  }

  // Method để toggle snap mode từ UI
  toggleSnapMode() {
    this.setSnapEnabled(!this._snapEnabled);
    return this._snapEnabled;
  }

  // Method để lấy thông tin snap hiện tại (cho UI)
  getCurrentSnapInfo() {
    if (!this._currentSnapTarget || !this._currentSnapPosition) {
      return null;
    }

    return {
      target: this._currentSnapTarget,
      position: this._currentSnapPosition,
      description: this.getSnapDescription(this._currentSnapPosition),
    };
  }

  // Helper method để lấy description của snap position
  getSnapDescription(snapType) {
    switch (snapType) {
      case this.SNAP_POSITIONS.LEFT_ALIGN:
        return 'Căn liền cạnh trái';
      case this.SNAP_POSITIONS.RIGHT_ALIGN:
        return 'Căn liền cạnh phải';
      case this.SNAP_POSITIONS.CENTER_ALIGN:
        return 'Căn giữa tâm';
      case this.SNAP_POSITIONS.EDGE_LEFT:
        return 'Thẳng cạnh trái';
      case this.SNAP_POSITIONS.EDGE_RIGHT:
        return 'Thẳng cạnh phải';
      case this.SNAP_POSITIONS.EDGE_FRONT:
        return 'Thẳng cạnh trước';
      case this.SNAP_POSITIONS.EDGE_BACK:
        return 'Thẳng cạnh sau';
      default:
        return '';
    }
  }

  // Method để force snap to nearest position (có thể gọi từ UI)
  snapToNearestPosition() {
    if (!this.selectedModel || !this._snapEnabled) return false;

    const currentPosition = this.selectedModel.position.clone();
    const nearestSnap = this.findNearestSnapPosition(
      this.selectedModel,
      currentPosition
    );

    if (nearestSnap) {
      this.selectedModel.position.copy(nearestSnap.position);
      this._lastValidPosition = nearestSnap.position.clone();

      // Update snap indicators
      this.clearSnapLines();
      this.createSnapLines(nearestSnap);
      this.showSnapIndicator(nearestSnap);

      this._currentSnapTarget = nearestSnap.target;
      this._currentSnapPosition = nearestSnap.type;

      return true;
    }

    return false;
  }

  // Method để get all possible snap positions for current selected model
  getAllSnapPositions() {
    if (!this.selectedModel) return [];

    const snapTargets = this.findSnapTargets(this.selectedModel);
    const allSnapPositions = [];

    for (const target of snapTargets) {
      const snapPositions = this.calculateSnapPositions(
        this.selectedModel,
        target
      );

      // Filter only valid positions
      const validSnapPositions = snapPositions.filter(snap =>
        this.isSnapPositionValid(this.selectedModel, snap.position, target)
      );

      allSnapPositions.push(...validSnapPositions);
    }

    return allSnapPositions;
  }

  setUIEvents({ onSelect, onDeselect, onDragging, onDragEnd }) {
    this._onUISelect = onSelect;
    this._onUIDeselect = onDeselect;
    this._onUIDragging = onDragging;
    this._onUIDragEnd = onDragEnd;
  }

  startDrag(event, axis = null, dragModelData) {
    event.preventDefault();
    if (!this.selectedModel) return;
    this.updateDragPlane(this.selectedModel.position.y);
    this.selectedModelData = dragModelData;

    setTimeout(() => {
      this._dragging = true;
      this._dragAxis = axis;
      this._lastValidPosition = this.selectedModel.position.clone();
      this._onUIDragging?.();

      const startPos = this.selectedModel.position.clone();
      const startScreenY = event.clientY;

      const onMove = moveEvent => {
        this.handleDragMove(moveEvent, startPos, startScreenY);
      };

      const onUp = upEvent => {
        this.handleDragEnd(upEvent, onMove, onUp);
      };

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    }, 0);
  }

  handleDragMove(event, startPos, startScreenY) {
    const rect = this.canvas.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );

    this.raycaster.setFromCamera(mouse, this.camera);
    const ground = this._dragPlane;
    const intersect = this.raycaster.intersectObject(ground)[0];

    const currentPos = this.selectedModel.position.clone();

    // if (this._dragAxis === 'xz' && intersect) {
    // const point = intersect.point;

    // // Tính độ chênh lệch ban đầu (nếu chưa có)
    // if (!this._initialOffset) {
    //   this._initialOffset = new THREE.Vector3(
    //     currentPos.x - point.x,
    //     0,
    //     currentPos.z - point.z
    //   );
    // }

    // // Tính vị trí mới
    // const targetPosition = new THREE.Vector3(
    //   point.x + this._initialOffset.x,
    //   currentPos.y,
    //   point.z + this._initialOffset.z
    // );

    // // Tìm vị trí trượt hợp lệ (bao gồm snap positioning)
    // const finalPosition = this.checkCollisionWithSlidingPosition(
    //   this.selectedModel,
    //   targetPosition
    // );

    // // Cập nhật vị trí
    // this.selectedModel.position.copy(finalPosition);
    // this._lastValidPosition = finalPosition.clone();

    if (this._dragAxis === 'xz' && intersect) {
      const point = intersect.point;

      // Tính độ chênh lệch ban đầu (nếu chưa có)
      if (!this._initialOffset) {
        this._initialOffset = new THREE.Vector3(
          currentPos.x - point.x,
          0,
          currentPos.z - point.z
        );
        // Lưu vị trí bắt đầu kéo để xác định trục khóa
        this._dragStartPosition = currentPos.clone();
      }

      // Tính vị trí mới
      let targetPosition = new THREE.Vector3(
        point.x + this._initialOffset.x,
        currentPos.y,
        point.z + this._initialOffset.z
      );

      // Nếu đang nhấn Shift, khóa trục
      if (event.shiftKey && this._dragStartPosition) {
        const deltaX = Math.abs(targetPosition.x - this._dragStartPosition.x);
        const deltaZ = Math.abs(targetPosition.z - this._dragStartPosition.z);

        // Khóa trục dựa trên hướng di chuyển lớn hơn
        if (deltaX > deltaZ) {
          // Khóa trục Z, chỉ di chuyển theo X
          targetPosition.z = this._dragStartPosition.z;
        } else {
          // Khóa trục X, chỉ di chuyển theo Z
          targetPosition.x = this._dragStartPosition.x;
        }
      }

      // Tìm vị trí trượt hợp lệ (bao gồm snap positioning)
      const finalPosition = this.checkCollisionWithSlidingPosition(
        this.selectedModel,
        targetPosition
      );

      // Cập nhật vị trí
      this.selectedModel.position.copy(finalPosition);
      this._lastValidPosition = finalPosition.clone();
    } else if (this._dragAxis === 'y') {
      const deltaY = event.clientY - startScreenY;
      const pixelsPerUnit = 100;
      const newY = startPos.y - deltaY / pixelsPerUnit;

      // Kiểm tra va chạm với sàn
      const floorY = this.getFloorLevel();
      const modelBoundingBox = this.getUpdatedBoundingBox(this.selectedModel);
      const modelBottomOffset =
        modelBoundingBox.min.y - this.selectedModel.position.y;
      const modelBottomY = newY + modelBottomOffset;

      // Tính toán vị trí Y hợp lệ - không cho phép đáy model đi xuống dưới sàn
      let validY = newY;
      if (modelBottomY < floorY) {
        // Nếu đáy model sẽ đi xuống dưới sàn, điều chỉnh vị trí Y
        validY = floorY - modelBottomOffset;
      }

      const targetPosition = new THREE.Vector3(
        currentPos.x,
        validY,
        currentPos.z
      );

      // Tìm vị trí trượt hợp lệ cho di chuyển theo trục Y (bao gồm snap positioning)
      const finalPosition = this.checkCollisionWithSlidingPosition(
        this.selectedModel,
        targetPosition
      );

      this.selectedModel.position.copy(finalPosition);
      this._lastValidPosition = finalPosition.clone();
    } else if (this._dragAxis === 'o') {
      const point = intersect.point;
      const orientation = GeometryUtils.getModuleOrientation(
        this.selectedModel
      );

      // Tính độ chênh lệch ban đầu (nếu chưa có)
      if (!this._initialOffset) {
        if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
          this._initialOffset = new THREE.Vector3(
            currentPos.x,
            0,
            currentPos.z - point.z
          );
        } else if (
          orientation === 'ROTATED_90' ||
          orientation === 'ROTATED_270'
        ) {
          this._initialOffset = new THREE.Vector3(
            currentPos.x - point.x,
            0,
            currentPos.z
          );
        }
      }

      // Tính vị trí mới
      let targetPosition;

      if (orientation === 'NORMAL' || orientation === 'ROTATED_180') {
        targetPosition = new THREE.Vector3(
          currentPos.x,
          currentPos.y,
          point.z + this._initialOffset.z
        );
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        targetPosition = new THREE.Vector3(
          point.x + this._initialOffset.x,
          currentPos.y,
          currentPos.z
        );
      }

      if (!targetPosition) return;

      // Tìm vị trí trượt hợp lệ (bao gồm snap positioning)
      const finalPosition = this.checkCollisionWithSlidingPosition(
        this.selectedModel,
        targetPosition
      );

      // Cập nhật vị trí
      this.selectedModel.position.copy(finalPosition);
      this._lastValidPosition = finalPosition.clone();
    }
  }

  handleDragEnd(event, onMove, onUp) {
    this._dragging = false;
    this._dragAxis = null;
    this._initialOffset = null;
    this._lastValidPosition = null;

    // Clear snap indicators when drag ends
    this.clearSnapLines();
    this.hideSnapIndicator();
    this._currentSnapTarget = null;
    this._currentSnapPosition = null;

    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    // this._onUIDragEnd?.({
    //   x: event.clientX - this._btnWidth,
    //   y: event.clientY - this._btnWidth,
    // });

    const pos = this.worldToScreen(this.selectedModel.position);
    this._onUIDragEnd?.({
      x: pos.x,
      y: pos.y,
    });

    this.updateDragPlane(-1);
  }

  worldToScreen(worldPosition) {
    // Clone vector để không thay đổi vector gốc
    const vector = worldPosition.clone();

    // Project vector từ world space sang normalized device coordinates (NDC)
    vector.project(this.camera);

    const canvasRect = this.canvas.getBoundingClientRect();

    // Chuyển từ NDC (-1 to 1) sang screen coordinates (pixels)
    const x = (vector.x * 0.5 + 0.5) * canvasRect.width;
    const y = (vector.y * -0.5 + 0.5) * canvasRect.height;

    // z value cho biết điểm có nằm trước camera không (z < 1 là visible)
    const z = vector.z;

    return {
      x: Math.round(x),
      y: Math.round(y),
      z: z,
      isVisible: z < 1 && z > -1, // Kiểm tra xem có nằm trong view frustum không
      canvasX: x,
      canvasY: y,
      screenX: x + canvasRect.left + window.scrollX, // Absolute screen position
      screenY: y + canvasRect.top + window.scrollY,
    };
  }

  isDragging() {
    return this._dragging;
  }

  getSelectedModel() {
    return this.selectedModel;
  }

  clearSelection() {
    this.selectedModel = null;

    // Clear snap indicators when selection is cleared
    this.clearSnapLines();
    this.hideSnapIndicator();
    this._currentSnapTarget = null;
    this._currentSnapPosition = null;

    this._onUIDeselect?.();
  }

  dispose() {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);

    // Clean up snap lines
    this.clearSnapLines();
    if (this._snapLinesMaterial) {
      this._snapLinesMaterial.dispose();
    }

    // Clean up drag plane
    if (this._dragPlane) {
      this.scene.remove(this._dragPlane);
      this._dragPlane.geometry.dispose();
      this._dragPlane.material.dispose();
    }

    // Clear references
    this.selectedModel = null;
    this._currentSnapTarget = null;
    this._currentSnapPosition = null;
    this._snapIndicatorCallback = null;

    // Clear callbacks
    this._onUISelect = null;
    this._onUIDeselect = null;
    this._onUIDragging = null;
    this._onUIDragEnd = null;
  }
}
